/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import { createIntl, createIntlCache } from 'react-intl';

import { Language } from '../../../ts/skill';
import messages from '../messages';

const purple20 = '#E8DAFF';
const purple50 = '#A56EFF';
const teal50 = '#009D9A';
const teal20 = '#9EF0F0';

export const unrecognizedPollingStatus = {
  AVAILABLE: 'Available',
  IN_PROGRESS: 'In Progress',
  NONE: 'None',
  UPDATING: 'Updating'
};

export const isValidNumber = num =>
  typeof num === 'number' && !Number.isNaN(num);

// Converts a number to a value with up to two decimal places and rounded.
// Eg. convertToTwoDecimals(0.876865, true)  // returns 88.69
// convertToTwoDecimals(99.99999, false) // returns 100
export const convertToTwoDecimals = (num, isPercentage) => {
  const multiplier = isPercentage ? 100 : 1;
  return parseFloat((num * multiplier).toFixed(2));
};

export const abbreviateNumber = (num: number, isPercentage?: boolean) => {
  if (isPercentage) {
    return num.toFixed(0) + '%';
  }
  if (num > 9999) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return Number(num).toLocaleString();
};

/**
 * Gets the chart legends that will be displayed underneath each chart.
 * @param {string} type denotes the type of chart (bar or line)
 * @param {Object} data total numbers to display for each legend
 * @param {boolean} isCompletion denotes completion or recognition charts
 * @param {string} language local language
 * @returns {array} array of chart legends
 */
export const getChartLegends = (
  type: string,
  data: any,
  isCompletion: boolean
) => {
  const cache = createIntlCache();
  const intl = createIntl(
    {
      messages: {},
      locale: Language.English
    },
    cache
  );

  if (isCompletion) {
    if (type === 'bar') {
      return [
        {
          label: intl.formatMessage(messages.completeActions) + ':',
          color: purple50,
          total: abbreviateNumber(data?.totalOne)
        },
        {
          label: intl.formatMessage(messages.incompleteActions) + ':',
          color: purple20,
          total: abbreviateNumber(data?.totalTwo)
        }
      ];
    } else {
      return [
        {
          label: intl.formatMessage(messages.completion),
          color: purple50
        },
        {
          label: intl.formatMessage(messages.noData),
          color: purple50,
          noData: true
        }
      ];
    }
  } else {
    //Recogintion data
    if (type === 'bar') {
      return [
        {
          label: intl.formatMessage(messages.recognizedRequests) + ':',
          color: teal50,
          total: abbreviateNumber(data?.totalOne)
        },
        {
          label: intl.formatMessage(messages.unrecognizedRequests) + ':',
          color: teal20,
          total: abbreviateNumber(data?.totalTwo)
        }
      ];
    } else {
      return [
        {
          label: intl.formatMessage(messages.recognition),
          color: teal50
        },
        {
          label: intl.formatMessage(messages.noData),
          color: teal50,
          noData: true
        }
      ];
    }
  }
};

/**
 * Generates the right structure of the chart data
 * @param {string} type denotes the type of chart (bar or line)
 * @param {Object} data datapoints for the chart
 * @param {boolean} isCompletion denotes completion or recognition charts
 * @param {string} language local language
 * @returns {object} structure of the chart data
 */
export const populateChartData = (
  type: string,
  data: any,
  isCompletion: boolean
) => {
  const cache = createIntlCache();
  const intl = createIntl(
    {
      messages: {},
      locale: Language.English
    },
    cache
  );
  const chartData: {
    allSeries?: any;
    dataColumn: string[];
    series: any;
  } = {
    dataColumn: [],
    series: []
  };
  if (isCompletion) {
    if (type === 'bar') {
      chartData.dataColumn = ['Date', 'Complete', 'Number of actions started'];
      chartData.series = [
        {
          data: data?.barOne,
          color: purple50,
          label: intl.formatMessage(messages.completeActions)
        },
        {
          data: data?.barTwo,
          color: purple20,
          label: intl.formatMessage(messages.incompleteActions)
        }
      ];
    } else {
      chartData.dataColumn = ['Date', 'Completion percentage', ''];
      chartData.series = [
        {
          data: data?.line,
          color: purple50,
          label: intl.formatMessage(messages.completion)
        }
      ];
      chartData.allSeries = [
        {
          title: intl.formatMessage(messages.completeActions),
          reports: data?.barOne
        },
        {
          title: intl.formatMessage(messages.incompleteActions),
          reports: data?.barTwo
        }
      ];
    }
  } else {
    //Recognition data
    if (type === 'bar') {
      chartData.dataColumn = ['Date', 'Recognition', 'Quantity'];
      chartData.series = [
        {
          data: data?.barOne,
          color: teal50,
          label: intl.formatMessage(messages.recognizedRequests)
        },
        {
          data: data?.barTwo,
          color: teal20,
          label: intl.formatMessage(messages.unrecognizedRequests)
        }
      ];
    } else {
      chartData.dataColumn = ['Date', 'Recognition', 'Quantity'];
      chartData.series = [
        {
          data: data?.line,
          color: teal50,
          label: intl.formatMessage(messages.recognition)
        }
      ];
      chartData.allSeries = [
        {
          title: intl.formatMessage(messages.recognizedRequests),
          reports: data?.barOne
        },
        {
          title: intl.formatMessage(messages.unrecognizedRequests),
          reports: data?.barTwo
        }
      ];
    }
  }
  return chartData;
};
